/**
 * Dashboard (S11).
 *
 * Three KPI cards, an activity feed and the lead breakdown — every figure
 * aggregated from data this console already owns, so nothing here waits on the
 * platform.
 *
 * ## Why a trend is four fields and not a percentage
 *
 * The mock renders a hardcoded "▲ 12% vs yesterday". Computing that honestly
 * turns out to need more than a number, because of the calling window: Schwarz
 * calls Tuesday to Thursday, so "yesterday" is usually a day on which no call
 * could have been placed. A card reading −100% every Tuesday would be
 * arithmetically correct and useless.
 *
 * So a trend carries what it compared against, in words, and `changePct` is
 * nullable — for the case where there is no comparable period at all. A console
 * one day old should say "no earlier data" rather than "up 100%".
 */
import { z } from 'zod';

import type { CallOutcome } from './enums.js';
import { LeadCategory } from './enums.js';

export const kpiTrendSchema = z.object({
  value: z.number().int().min(0),
  previous: z.number().int().min(0),
  /**
   * Percentage change, or null when it cannot be stated.
   *
   * Null covers two cases that both matter: no earlier period exists, and the
   * earlier period was zero — where every non-zero present would be an infinite
   * rise. "Up from none" is a sentence; "+∞%" is not.
   */
  changePct: z.number().nullable(),
  /** What `previous` was, in the words the card should use: "Thursday", "the prior 7 days". */
  comparedTo: z.string(),
});
export type KpiTrend = z.infer<typeof kpiTrendSchema>;

/** One bar of the lead breakdown. */
export const leadBreakdownRowSchema = z.object({
  category: LeadCategory.schema,
  count: z.number().int().min(0),
});
export type LeadBreakdownRow = z.infer<typeof leadBreakdownRowSchema>;

/**
 * What happened, for the activity feed.
 *
 * Deliberately structured rather than a rendered sentence. The server knows what
 * occurred; the wording belongs with the rest of the labels, where a change to a
 * phrase does not mean a change to an API.
 */
export const ACTIVITY_KINDS = [
  'CALL_ANSWERED',
  'CALL_MISSED',
  'CALL_FAILED',
  'INTERACTION_ANALYSED',
  'CONTACT_SUPPRESSED',
  'AGENT_UPDATED',
] as const;
export type ActivityKind = (typeof ACTIVITY_KINDS)[number];
export const activityKindSchema = z.enum(ACTIVITY_KINDS);

export const activityItemSchema = z.object({
  /** Composite — `call:<id>`, `interaction:<id>` — because rows come from three tables. */
  id: z.string(),
  kind: activityKindSchema,
  /** The contact, or the agent's preset name. Rendered emphasised, as in the mock. */
  subject: z.string(),
  /** Company, lead category, or the fields that changed. Null when there is nothing to add. */
  detail: z.string().nullable(),
  linkType: z.enum(['CONTACT', 'INTERACTION', 'AGENT', 'NONE']),
  linkId: z.string().nullable(),
  at: z.string().datetime(),
});
export type ActivityItem = z.infer<typeof activityItemSchema>;

/** How many feed rows the dashboard asks for. */
export const ACTIVITY_LIMIT = 12;

export const dashboardSchema = z.object({
  /** Calls placed today against the last day calling was permitted. */
  callsToday: kpiTrendSchema,
  /** Hot and warm leads over the last 7 days against the 7 before that. */
  hotLeads: kpiTrendSchema,
  warmLeads: kpiTrendSchema,
  /**
   * Every category, including those with no rows.
   *
   * A breakdown that omitted empty categories would let "no do-not-call requests
   * this period" and "we are not counting do-not-call requests" look identical.
   */
  leadBreakdown: z.array(leadBreakdownRowSchema),
  activity: z.array(activityItemSchema),
  /** In flight right now, for the feed's live indicator. */
  callsInFlight: z.number().int().min(0),
  /** Waiting in the queue, so a quiet dashboard can distinguish idle from empty. */
  queued: z.number().int().min(0),
});
export type Dashboard = z.infer<typeof dashboardSchema>;

/** Outcomes that count as a call having been placed, for the KPI. */
export const PLACED_OUTCOMES: readonly CallOutcome[] = [
  'ANSWERED',
  'MISSED',
  'FAILED',
  'DIALING',
] as const;
